import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, TrendingUp, TrendingDown } from "lucide-react";
import MoneyInput from "@/components/MoneyInput";

export default function QuickPaymentDialog({ open, onOpenChange, onDone }) {
  const [cariler, setCariler] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    type: "gider", amount: 0, category: "Ödeme", date: todayISO(),
    cari_id: "", project_id: "", description: "",
  });

  useEffect(() => {
    if (open) {
      setForm({ type: "gider", amount: 0, category: "Ödeme", date: todayISO(), cari_id: "", project_id: "", description: "" });
      (async () => {
        try {
          const [{ data: c }, { data: p }] = await Promise.all([
            api.get("/cariler"), api.get("/projects"),
          ]);
          setCariler(c); setProjects(p);
        } catch {}
      })();
    }
  }, [open]);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/transactions", {
        type: form.type,
        amount: Number(form.amount),
        category: form.category || (form.type === "gider" ? "Ödeme" : "Tahsilat"),
        date: form.date,
        description: form.description,
        cari_id: form.cari_id || null,
        project_id: form.project_id || null,
      });
      toast.success(form.type === "gider" ? "Ödeme kaydedildi" : "Tahsilat kaydedildi");
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="quick-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Hızlı Ödeme</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setForm({ ...form, type: "gider", category: "Ödeme" })}
              data-testid="qp-type-gider"
              className={`p-3 rounded-md border transition-all flex items-center gap-2 justify-center text-sm font-medium ${
                form.type === "gider"
                  ? "border-[hsl(var(--danger))] bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]"
                  : "border-border hover:bg-secondary"
              }`}
            >
              <TrendingDown className="w-4 h-4" /> Gider (Ödeme)
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, type: "gelir", category: "Tahsilat" })}
              data-testid="qp-type-gelir"
              className={`p-3 rounded-md border transition-all flex items-center gap-2 justify-center text-sm font-medium ${
                form.type === "gelir"
                  ? "border-[hsl(var(--success))] bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]"
                  : "border-border hover:bg-secondary"
              }`}
            >
              <TrendingUp className="w-4 h-4" /> Gelir (Tahsilat)
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Tutar (₺)</Label>
              <MoneyInput data-testid="qp-amount" autoFocus value={form.amount} onChange={(n) => setForm({ ...form, amount: n })} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Tarih</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Cari (opsiyonel)</Label>
            <Select value={form.cari_id || "none"} onValueChange={(v) => setForm({ ...form, cari_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="qp-cari"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Yok —</SelectItem>
                {cariler.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} · {c.type === "musteri" ? "Müşteri" : c.type === "taseron" ? "Taşeron" : "Tedarikçi"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Kategori</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Proje</Label>
              <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Yok —</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Açıklama</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Opsiyonel" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
          <Button data-testid="qp-submit" onClick={submit} disabled={!form.amount || loading}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
